import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle } from "@/components/ui/SectionTitle";
import {
  CalendarDays,
  CalendarX2,
  CheckCircle2,
  Download,
  Eye,
  Paperclip,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import "react-quill-new/dist/quill.snow.css";
import {
  useAcknowledgeCompanyPolicyMutation,
  useGetCompanyPolicyByIdQuery,
} from "@/services/(my-space)/api.company-policies";
import toast from "react-hot-toast";
import { ENUM_MS_COMAPNY_POLICIES_STATUS } from "@/utils/enums/company-policies.enum";
import { PrimaryDashedSvg } from "@/assets/svgs";
import { useState, type ElementType } from "react";
import { startCase } from "lodash";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useFileDownloader } from "@/hooks/useFileDownloader";
import { PermissionButton } from "@/components/ui/permission-button";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";

const getIconColor = (color: string) => {
  const colors: Record<string, string> = {
    "green-400": "34,197,94",
    "red-400": "239,68,68",
    "blue-400": "59,130,246",
    "orange-400": "251,146,60",
    "gray-400": "156,163,175",
  };
  return colors[color] || "156,163,175";
};

/** Policies download as PDF; the extension is shown after the title. */
const POLICY_FILE_EXTENSION = ".pdf";

export const MySpaceCompanyPoliciesSummary = () => {
  const { t } = useTranslation("my-space");
  const navigate = useNavigate();
  const { companyPoliciesId } = useParams();
  const { downloadFiles } = useFileDownloader();

  const [isChecked, setIsChecked] = useState(false);

  const {
    data: companyPolicyData,
    isLoading,
    isError,
  } = useGetCompanyPolicyByIdQuery(
    { companyPoliciesId: String(companyPoliciesId) },
    { skip: !companyPoliciesId },
  );
  const [acknowledgePolicy, { isLoading: isAcknowledging }] =
    useAcknowledgeCompanyPolicyMutation();

  const companyPolicy = companyPolicyData?.data;

  const overviewFields = companyPolicy
    ? [
        { label: t("ui.policy_title"), value: companyPolicy.title || "-" },
        {
          label: t("shared.category"),
          value: companyPolicy.category.name || "-",
        },
        {
          label: t("ui.description"),
          value: companyPolicy.summary || "-",
          className: "col-span-2",
        },
      ]
    : [];

  const handleDownload = (policyUrl: string) => {
    const urls = [policyUrl].filter(Boolean) as string[];
    if (urls.length > 0) downloadFiles(urls);
  };

  const handleAcknowledge = async () => {
    if (!companyPoliciesId) return;
    const id = toast.loading("Processing...");
    await acknowledgePolicy({ companyPoliciesId: String(companyPoliciesId) })
      .unwrap()
      .then((res) => toast.success(res.message, { id }))
      .catch((e) =>
        toast.error(
          e?.data?.message ??
            e.data?.errors?.message ??
            t("shared.generic_error"),
          { id },
        ),
      );
  };

  if (isLoading)
    return (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-2 lg:p-6 bg-white shadow-xs border h-full">
        <Card className="py-4 lg:p-4 col-span-2 flex flex-col gap-4">
          <CardContent className="p-2 lg:p-0">
            <Skeleton className="w-36 sm:w-48 h-6 mb-4" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Skeleton className="h-16 w-full rounded-sm" />
              <Skeleton className="h-16 w-full rounded-sm" />
            </div>
            <Skeleton className="h-10 w-full mt-4 rounded-md" />
            <Skeleton className="min-h-[30vh] w-full mt-4 rounded-md" />
          </CardContent>
        </Card>
        <Card className="p-0 h-fit">
          <CardContent className="p-2 lg:p-0">
            <Skeleton className="w-28 sm:w-36 h-6 mb-4" />
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="flex justify-between items-center gap-2 sm:gap-4 mt-2 py-2 px-4 sm:px-8"
              >
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-5 w-20 sm:w-24 rounded-sm" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );

  if (!companyPolicy || isError) {
    toast.error(t("toasts.company_policy_not_found"));
    navigate(-1);
    return;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-4 p-2 lg:p-6 bg-white rounded-sm h-full max-h-[calc(100vh-80px)] overflow-hidden">
      {/* LEFT CARD */}
      <Card className="col-span-2 flex flex-col mb-4 lg:mb-0 overflow-hidden h-full">
        <CardContent className="p-0 flex flex-col h-full overflow-hidden">
          <div className="px-6 py-4 border-b shrink-0">
            <SectionTitle
              title={t("company_policy.policy_overview")}
              size="md"
            />
          </div>
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto scrollbar-thin px-6 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
              {overviewFields.map(({ label, value, className }) => (
                <div
                  key={label}
                  className={`flex flex-col gap-1 bg-gray-100 p-2 rounded-sm ${className}`}
                >
                  <p className="text-[10px] sm:text-xs text-black/50 uppercase">
                    {label}
                  </p>
                  <p className="text-xs sm:text-sm font-medium">{value}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 bg-gray-100 p-2 rounded-sm">
              <p className="text-[10px] sm:text-xs text-black/50 uppercase mb-1">
                {t("company_policy.policy_content")}
              </p>
              <div className="prose max-w-full">
                <div
                  className="ql-editor"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(companyPolicy.content || "-"),
                  }}
                />
              </div>
            </div>
          </div>

          {/* Sticky attachments */}
          <div className="p-6 border-t shrink-0">
            <p className="text-[10px] sm:text-xs text-black/50 uppercase mb-2">
              {t("company_policy.attachments")}
            </p>
            <div
              className={`flex items-center justify-between rounded-sm p-2 overflow-hidden w-1/2 transition ${
                companyPolicy?.attachmentUrl
                  ? "bg-gray-50 hover:bg-gray-100 cursor-pointer"
                  : "bg-gray-100 cursor-not-allowed opacity-60"
              }`}
              onClick={() => {
                if (!companyPolicy?.attachmentUrl) return;
                handleDownload(companyPolicy.attachmentUrl!);
              }}
            >
              <div className="flex items-center gap-3">
                <div className="bg-red-100 p-2 rounded-xs">
                  <Paperclip className="text-red-500 size-4" />
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {`${companyPolicy.title}${POLICY_FILE_EXTENSION}`}
                  </p>
                  <p className="text-xs text-gray-400">
                    {companyPolicy?.attachmentUrl
                      ? t("ui.click_to_download")
                      : t("ui.no_attachment_available")}
                  </p>
                </div>
              </div>
              <Download
                className={`size-4 ${companyPolicy?.attachmentUrl ? "text-primary" : "text-gray-400"}`}
              />
            </div>
          </div>

          {/* Sticky acknowledge footer */}
          <div className="flex justify-between border-t px-6 py-4 shrink-0">
            <div className="flex items-center gap-2">
              <Checkbox
                id="isAcknowledged"
                name="isAcknowledged"
                checked={isChecked || companyPolicy.isAcknowledged}
                disabled={companyPolicy.isAcknowledged}
                onCheckedChange={(checked) => setIsChecked(Boolean(checked))}
              />
              <Label className="normal-case text-black">
                {t(
                  "my-space:ui.i_confirm_that_i_have_read_and_understood_the_policy",
                )}
              </Label>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                className="text-gray-400 hover:text-gray-500"
                onClick={() => navigate(-1)}
              >
                {t("ui.cancel")}
              </Button>
              <span
                data-tooltip-id="hrm-tooltip"
                data-tooltip-content={
                  companyPolicy.isAcknowledged
                    ? t("ui.policy_is_already_acknowledged")
                    : undefined
                }
              >
                <PermissionButton
                  section={PLATFORM_SECTION_ENUM.COMPANY_POLICIES}
                  action="edit"
                  variant="primary"
                  onClick={handleAcknowledge}
                  disabled={
                    !isChecked ||
                    isAcknowledging ||
                    companyPolicy.isAcknowledged
                  }
                >
                  {t("ui.acknowledge_policy")}
                </PermissionButton>
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* RIGHT CARD */}
      <Card className="p-0 h-fit">
        <CardContent className="p-1 lg:p-0">
          <div className="flex items-center justify-between border-b p-4">
            <h6 className="font-semibold flex items-center gap-2 text-sm sm:text-lg">
              {t("company_policy.policy_details")}
            </h6>
          </div>
          <DetailRow
            icon={Eye}
            label={t("shared.status")}
            customContent={<StatusPill status={companyPolicy.status} />}
            iconColor="orange-400"
          />
          <DetailRow
            icon={CalendarDays}
            label={t("company_policy.effective_date")}
            value={
              companyPolicy.effectiveDate
                ? format(new Date(companyPolicy.effectiveDate), "dd/MM/yyyy")
                : "-"
            }
            iconColor="green-400"
          />
          <DetailRow
            icon={CalendarX2}
            label={t("company_policy.expiry_date")}
            value={
              companyPolicy.expiryDate
                ? format(new Date(companyPolicy.expiryDate), "dd/MM/yyyy")
                : "-"
            }
            iconColor="red-400"
          />
        </CardContent>
      </Card>
    </div>
  );
};

interface DetailRowProps {
  icon: any;
  label: string;
  value?: string;
  valueClass?: string;
  iconColor?: string;
  customContent?: React.ReactNode;
}

const DetailRow = ({
  icon: Icon,
  label,
  value,
  valueClass = "",
  iconColor = "gray-400",
  customContent,
}: DetailRowProps) => (
  <div className="flex justify-between items-center gap-2 sm:gap-4 mt-2 p-2 sm:p-3 lg:py-3 lg:px-8">
    <div className="flex gap-2 items-center">
      <div
        className="flex h-8 w-8 sm:h-8 sm:w-8 items-center justify-center rounded-sm"
        style={{ backgroundColor: `rgba(${getIconColor(iconColor)},0.1)` }}
      >
        <Icon size={16} className={`text-${iconColor} sm:text-${iconColor}`} />
      </div>
      <p className="text-[10px] sm:text-xs text-black/50 uppercase font-semibold">
        {label}
      </p>
    </div>
    <div className={`px-2 py-1 sm:px-3 sm:py-1 rounded-sm ${valueClass}`}>
      {customContent || (
        <p className="font-medium text-[10px] sm:text-sm">{value}</p>
      )}
    </div>
  </div>
);

const StatusPill = ({
  status,
}: {
  status: ENUM_MS_COMAPNY_POLICIES_STATUS;
}) => {
  const statusPills: Record<
    ENUM_MS_COMAPNY_POLICIES_STATUS,
    { icon: ElementType; className: string }
  > = {
    [ENUM_MS_COMAPNY_POLICIES_STATUS.ACKNOWLEDGED]: {
      icon: CheckCircle2,
      className: "bg-green-100 text-green-800",
    },
    [ENUM_MS_COMAPNY_POLICIES_STATUS.OVERDUE]: {
      icon: PrimaryDashedSvg,
      className: "bg-red-200 text-red-600",
    },
    [ENUM_MS_COMAPNY_POLICIES_STATUS.PENDING]: {
      icon: PrimaryDashedSvg,
      className: "bg-orange-100 text-orange-800",
    },
  };
  const fallback = {
    icon: PrimaryDashedSvg,
    className: "bg-gray-100 text-gray-700",
  };
  const { icon: Icon, className } = statusPills[status!] ?? fallback;
  return (
    <Badge className={`font-medium ${className}`}>
      {Icon && <Icon className="size-4 me-1" />}
      {startCase(status)}
    </Badge>
  );
};
